import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { authApi, setAccessToken, USE_MOCK_API } from '../api/client';
import { userApi } from '../api/usersApi';
import { roleAssignmentsApi } from '../api/roleAssignmentsApi';
import { MODULE_KEYS, normalizeRoleScope, resolveRoleIdFromValue } from '../config/rbacConfig';
import { useCollectionSync } from '../hooks/useCollectionSync';
import { extractScopeSeed } from '../utils/scopeFields';

const AuthContext = createContext(null);
const SESSION_KEY = 'nlims.session';

const MODULE_SCOPE_MAP = {
  mod_1: MODULE_KEYS.POPULATION,
  mod_2: MODULE_KEYS.MARKETS,
  mod_3: MODULE_KEYS.INFRASTRUCTURE,
  mod_4: MODULE_KEYS.WORKFORCE,
  mod_5: MODULE_KEYS.PLANNING,
  mod_6: MODULE_KEYS.CONFLICTS,
  mod_7: MODULE_KEYS.ANIMAL_IDENTIFICATION,
  mod_8: MODULE_KEYS.ANIMAL_HEALTH,
};

function normalizeModuleKey(value) {
  const text = String(value ?? '').trim();
  if (!text) return null;
  if (Object.values(MODULE_KEYS).includes(text)) return text;
  if (MODULE_SCOPE_MAP[text]) return MODULE_SCOPE_MAP[text];
  if (MODULE_SCOPE_MAP[text.toLowerCase()]) return MODULE_SCOPE_MAP[text.toLowerCase()];
  const compact = text.toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (!compact) return null;

  if (
    compact === '1' ||
    compact === 'mod1' ||
    compact === 'module1' ||
    compact.includes('livestockpopulation') ||
    compact.includes('populationrecords') ||
    compact === 'population'
  ) {
    return MODULE_KEYS.POPULATION;
  }

  if (
    compact === '2' ||
    compact === 'mod2' ||
    compact === 'module2' ||
    compact.includes('marketstrade') ||
    compact === 'markets'
  ) {
    return MODULE_KEYS.MARKETS;
  }

  if (
    compact === '3' ||
    compact === 'mod3' ||
    compact === 'module3' ||
    compact.includes('infrastructure') ||
    compact.includes('services')
  ) {
    return MODULE_KEYS.INFRASTRUCTURE;
  }

  if (
    compact === '4' ||
    compact === 'mod4' ||
    compact === 'module4' ||
    compact.includes('workforce') ||
    compact.includes('institutional') ||
    compact.includes('actor')
  ) {
    return MODULE_KEYS.WORKFORCE;
  }

  if (
    compact === '5' ||
    compact === 'mod5' ||
    compact === 'module5' ||
    compact.includes('planning')
  ) {
    return MODULE_KEYS.PLANNING;
  }

  if (
    compact === '6' ||
    compact === 'mod6' ||
    compact === 'module6' ||
    compact.includes('conflict') ||
    compact.includes('dispute')
  ) {
    return MODULE_KEYS.CONFLICTS;
  }

  if (
    compact === '7' ||
    compact === 'mod7' ||
    compact === 'module7' ||
    compact.includes('animalidentification')
  ) {
    return MODULE_KEYS.ANIMAL_IDENTIFICATION;
  }

  if (
    compact === '8' ||
    compact === 'mod8' ||
    compact === 'module8' ||
    compact.includes('animalhealth')
  ) {
    return MODULE_KEYS.ANIMAL_HEALTH;
  }

  return null;
}

function extractUserAssignedModules(user = {}) {
  const tokens = [];

  const pushTokens = (value) => {
    if (value === null || value === undefined) return;
    if (Array.isArray(value)) {
      value.forEach((entry) => pushTokens(entry));
      return;
    }
    if (typeof value === 'object') {
      pushTokens(
        value.module_scope ??
        value.moduleScope ??
        value.module ??
        value.moduleKey ??
        value.module_key ??
        value.module_code ??
        value.moduleCode ??
        value.module_id ??
        value.moduleId ??
        value.code ??
        value.slug ??
        value.name ??
        value.label
      );
      return;
    }

    String(value)
      .split(/[,\s|;]+/)
      .forEach((token) => {
        if (token) tokens.push(token);
      });
  };

  pushTokens(user.assignedModules);
  pushTokens(user.assigned_modules);
  pushTokens(user.module_scope);
  pushTokens(user.moduleScope);
  pushTokens(user.module_assignments);
  pushTokens(user.moduleAssignments);
  pushTokens(user.role_assignment);
  pushTokens(user.roleAssignment);
  pushTokens(user.role_assignments);
  pushTokens(user.roleAssignments);
  pushTokens(user.assignment);
  pushTokens(user.assignments);

  if (Array.isArray(user.roles)) {
    user.roles.forEach((role) => {
      pushTokens(role?.module_scope);
      pushTokens(role?.moduleScope);
      pushTokens(role?.module_assignments);
      pushTokens(role?.moduleAssignments);
      pushTokens(role?.role_assignment);
      pushTokens(role?.roleAssignment);
      pushTokens(role?.assignments);
    });
  }

  return [...new Set(tokens.map(normalizeModuleKey).filter(Boolean))];
}

function getModuleScopeFromAssignments(assignments = []) {
  const scopes = [...new Set(
    assignments
      .map((assignment) => assignment?.module_scope ?? assignment?.moduleScope ?? '')
      .filter(Boolean)
      .map((value) => String(value).trim())
      .filter(Boolean)
  )];

  return scopes.length > 0 ? scopes.join(',') : null;
}

function attachAssignmentContext(user = {}, assignments = []) {
  const existingAssignments = [
    ...(Array.isArray(user.role_assignments) ? user.role_assignments : []),
    ...(Array.isArray(user.roleAssignments) ? user.roleAssignments : []),
  ];
  const activeAssignments = Array.isArray(assignments)
    ? assignments.filter((assignment) => assignment?.is_active !== false)
    : [];
  const mergedAssignments = activeAssignments.length > 0 ? activeAssignments : existingAssignments;

  const moduleScope = user.module_scope || user.moduleScope || getModuleScopeFromAssignments(mergedAssignments) || null;
  const institutionUuid =
    user.institutionUuid ??
    user.institution_uuid ??
    mergedAssignments.find((assignment) => assignment?.institution_uuid)?.institution_uuid ??
    null;

  return {
    ...user,
    institutionUuid,
    institution_uuid: institutionUuid,
    role_assignments: mergedAssignments,
    roleAssignments: mergedAssignments,
    module_scope: moduleScope,
    moduleScope,
  };
}

async function loadRoleAssignmentsForUser(userUuid) {
  if (!userUuid) return [];

  try {
    const assignments = await roleAssignmentsApi.list({ per_page: 500, page: 1, user_uuid: userUuid });
    return Array.isArray(assignments)
      ? assignments.filter((assignment) => {
          const assignmentUserUuid = assignment?.user_uuid ?? assignment?.user_id ?? null;
          return !assignmentUserUuid || String(assignmentUserUuid) === String(userUuid);
        })
      : [];
  } catch {
    return [];
  }
}

function normalizeSessionScope(session) {
  if (!session?.user) return session;

  const user = session.user;
  const resolvedRoleId = resolveRoleIdFromValue(
    user.roleId ??
    user.role_id ??
    user.role ??
    user.roles?.[0] ??
    user.role_code ??
    user.roleCode ??
    user.role_name ??
    user.roleName ??
    user.role_label ??
    user.roleLabel ??
    user.backend_class ??
    user.backendClass ??
    null
  );
  const scopeSeed = extractScopeSeed(user);
  const scope = normalizeRoleScope(resolvedRoleId ?? user.roleId, scopeSeed);
  const assignedModules = extractUserAssignedModules(user);
  const moduleScope = user.module_scope || user.moduleScope || assignedModules.join(',') || null;

  return {
    ...session,
    user: {
      ...user,
      roleId: resolvedRoleId ?? user.roleId ?? null,
      role_id: resolvedRoleId ?? user.role_id ?? user.roleId ?? null,
      state: scope.state ?? null,
      lga: scope.lga ?? null,
      ward: scope.ward ?? null,
      scope,
      scopeLevel: scope.level ?? null,
      assignedState: scope.state ?? null,
      assignedLga: scope.lga ?? null,
      assignedWard: scope.ward ?? null,
      assignedModules,
      assigned_modules: assignedModules,
      module_scope: moduleScope,
      moduleScope,
    },
  };
}

export function readStoredSession() {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY) || window.sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;

    const parsed = normalizeSessionScope(JSON.parse(raw));
    if (parsed?._isMock && !USE_MOCK_API) {
      window.localStorage.removeItem(SESSION_KEY);
      window.sessionStorage.removeItem(SESSION_KEY);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function hasPersistedSession() {
  const stored = readStoredSession();
  return Boolean(stored?.accessToken && stored?.user);
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => {
    const stored = readStoredSession();
    if (stored?.accessToken) setAccessToken(stored.accessToken);
    return stored;
  });
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(session);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const persistSession = useCallback((nextSession, keepLoggedIn = false) => {
    const storage = keepLoggedIn ? window.localStorage : window.sessionStorage;
    window.localStorage.removeItem(SESSION_KEY);
    window.sessionStorage.removeItem(SESSION_KEY);
    storage.setItem(SESSION_KEY, JSON.stringify(nextSession));
  }, []);

  const refreshAssignedModules = useCallback(async () => {
    const currentSession = sessionRef.current;
    const currentUser = currentSession?.user;
    if (!currentUser?.uuid || currentSession?._isMock) return;

    try {
      const profile = await userApi.me();
      const liveAssignments = await loadRoleAssignmentsForUser(profile?.uuid ?? currentUser.uuid);
      const mergedProfile = attachAssignmentContext(
        {
          ...currentSession.user,
          ...profile,
        },
        liveAssignments
      );
      const assignedModules = extractUserAssignedModules(mergedProfile);
      const moduleScope = mergedProfile.module_scope || mergedProfile.moduleScope || assignedModules.join(',') || null;
      const nextSession = normalizeSessionScope({
        ...currentSession,
        user: {
          ...mergedProfile,
          assignedModules,
          assigned_modules: assignedModules,
          module_scope: moduleScope,
          moduleScope: moduleScope,
        },
      });

      setSession(nextSession);
      persistSession(nextSession, Boolean(window.localStorage.getItem(SESSION_KEY)));
    } catch {
      // Keep the existing session if the live assignment refresh temporarily fails.
    }
  }, [persistSession]);

  useCollectionSync('role-assignments', () => {
    void refreshAssignedModules();
  });

  useEffect(() => {
    if (!session?.user?.uuid || session?._isMock) return;
    void refreshAssignedModules();
  }, [refreshAssignedModules, session?.user?.uuid, session?._isMock]);

  const login = async (credentials, options = {}) => {
    setLoading(true);
    try {
      const nextSession = await authApi.login(credentials);
      setAccessToken(nextSession.accessToken);
      let liveUser = nextSession?.user ?? null;

      const roleIdFromLogin = liveUser?.roleId ?? null;

      try {
        const profile = await userApi.me();
        if (profile?.uuid) {
          const profileScope = extractScopeSeed(profile);
          liveUser = {
            ...liveUser,
            ...profile,
            roleId: profile.role_id ?? profile.roleId ?? liveUser?.roleId ?? null,
            roleCode: profile.role_code ?? profile.roleCode ?? liveUser?.roleCode ?? null,
            roleName: profile.role_label ?? profile.roleLabel ?? liveUser?.roleName ?? null,
            backendClass: profile.backendClass ?? profile.backend_class ?? liveUser?.backendClass ?? null,
            state: profileScope.state ?? liveUser?.state ?? null,
            lga: profileScope.lga ?? liveUser?.lga ?? null,
            ward: profileScope.ward ?? liveUser?.ward ?? null,
          };

          // eslint-disable-next-line no-console
          console.group('[NLIMS Auth] /v1/user profile merge for:', profile.email ?? liveUser?.email);
          console.log('roleId resolved at /auth/login:', roleIdFromLogin);
          console.log('raw /v1/user profile:', profile);
          console.log('profile.role_id (post-normalizeUser):', profile.role_id);
          console.log('roleId AFTER merge (used for the rest of the session):', liveUser.roleId);
          if (roleIdFromLogin && liveUser.roleId !== roleIdFromLogin) {
            console.warn('[NLIMS Auth] roleId changed after merging /v1/user — this is the likely source of any scope/visibility bug.');
          }
          console.groupEnd();
        }
      } catch {
        // If the profile lookup fails, keep the login payload from the auth endpoint.
      }

      const liveAssignments = await loadRoleAssignmentsForUser(liveUser?.uuid ?? nextSession?.user?.uuid);
      const mergedLiveUser = attachAssignmentContext(liveUser, liveAssignments);
      const assignedModules = extractUserAssignedModules(mergedLiveUser);
      const moduleScope = mergedLiveUser.module_scope || mergedLiveUser.moduleScope || assignedModules.join(',') || null;
      const enrichedSession = normalizeSessionScope(
        {
          ...nextSession,
          user: {
            ...mergedLiveUser,
            assignedModules,
            assigned_modules: assignedModules,
            module_scope: moduleScope,
            moduleScope: moduleScope,
          },
        }
      );
      setSession(enrichedSession);
      persistSession(enrichedSession, options.keepLoggedIn);
      return enrichedSession;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    // Skip the backend logout call for mock sessions — token is not real
    if (!session?._isMock) {
      await authApi.logout();
    }
    window.localStorage.removeItem(SESSION_KEY);
    window.sessionStorage.removeItem(SESSION_KEY);
    setSession(null);
  };

  const value = useMemo(() => ({
    session,
    user:            session?.user        ?? null,
    permissions:     session?.permissions ?? [],
    token:           session?.accessToken ?? null,
    isAuthenticated: Boolean(session?.accessToken && session?.user),
    loading,
    login,
    logout,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [session, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
